import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { MessageSquarePlus, Users, Send, Quote, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { getContributions, createContribution } from "@/lib/api";
import { T, useLanguage } from "@/context/LanguageContext";

const EMPTY = { kind: "story", title: "", content: "", author_name: "", author_role: "", photo_url: "", source_url: "", contact_email: "" };
const KINDS = [
  { value: "story", label: "Local story" },
  { value: "correction", label: "Suggest a correction" },
  { value: "event", label: "Submit an event" },
  { value: "organization", label: "Add an organization" },
  { value: "photo", label: "Share a photo" },
  { value: "accessibility", label: "Report an issue" },
];

export function CommunityContributions({ geoId, placeName }) {
  const { languages, lang } = useLanguage();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [language, setLanguage] = useState(lang.code);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!geoId) return;
    getContributions(geoId).then((d) => setItems((d.contributions || []).filter((c) => !c.module))).catch(() => {});
    setForm(EMPTY);
    setOpen(false);
  }, [geoId]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.content.trim() || !form.author_name.trim()) {
      toast.error("Please add a title, your story and your name.");
      return;
    }
    setSubmitting(true);
    try {
      await createContribution({ geo_id: geoId, language, ...form });
      setForm(EMPTY);
      setOpen(false);
      toast.success("Thank you! Your submission is awaiting review and will appear once approved.");
    } catch (err) {
      toast.error("Could not submit right now. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const langName = (code) => languages.find((l) => l.code === code)?.name || code;

  return (
    <section className="mb-10" data-testid="community-contributions">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h2 className="flex items-center gap-2.5 font-display text-2xl font-bold text-navy">
          <Users className="h-6 w-6 text-oldglory-red" strokeWidth={1.6} />
          <T>Community Stories</T>
          {items.length > 0 && (
            <span className="rounded-full bg-navy/5 px-2.5 py-0.5 text-xs font-semibold text-navy">{items.length}</span>
          )}
        </h2>
        <button
          onClick={() => setOpen((o) => !o)}
          className="inline-flex items-center gap-2 rounded-full bg-oldglory-red px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-oldglory-dark"
          data-testid="contribute-toggle-btn"
        >
          <MessageSquarePlus className="h-4 w-4" /> <T>{open ? "Close" : "Share a local story"}</T>
        </button>
      </div>

      <p className="mb-4 text-sm text-muted-foreground">
        <T>Residents and locals: share history, traditions, photos and stories about</T>{" "}
        <span className="font-medium text-navy">{placeName}</span>. <T>Contribute in any language.</T>
      </p>

      {open && (
        <motion.form
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          onSubmit={submit}
          className="mb-6 space-y-3 rounded-xl border border-border bg-white p-5"
          data-testid="contribution-form"
        >
          <Field label="Type of contribution" required>
            <select value={form.kind} onChange={set("kind")} className={inputCls} data-testid="contrib-kind">
              {KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
            </select>
          </Field>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Your name" required>
              <input value={form.author_name} onChange={set("author_name")} className={inputCls} placeholder="Jane Doe"
                data-testid="contrib-author" />
            </Field>
            <Field label="Your connection (optional)">
              <input value={form.author_role} onChange={set("author_role")} className={inputCls} placeholder="Lifelong resident, historian…"
                data-testid="contrib-role" />
            </Field>
          </div>
          <Field label="Title" required>
            <input value={form.title} onChange={set("title")} className={inputCls} placeholder="A story worth telling…"
              data-testid="contrib-title" />
          </Field>
          <Field label="Your story" required>
            <textarea value={form.content} onChange={set("content")} rows={5} className={inputCls}
              placeholder="Share what makes this place special…" data-testid="contrib-content" />
          </Field>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Language">
              <select value={language} onChange={(e) => setLanguage(e.target.value)} className={inputCls} data-testid="contrib-language">
                {languages.map((l) => (
                  <option key={l.code} value={l.code}>{`${l.name} — ${l.native_name}`}</option>
                ))}
              </select>
            </Field>
            <Field label="Photo URL (optional)">
              <input value={form.photo_url} onChange={set("photo_url")} className={inputCls} placeholder="https://…"
                data-testid="contrib-photo" />
            </Field>
          </div>
          <Field label="Source link (optional, strengthens review)">
            <input value={form.source_url} onChange={set("source_url")} className={inputCls} placeholder="https://official-source…"
              data-testid="contrib-source" />
          </Field>
          <Field label="Your email (optional — we'll let you know when it's approved)">
            <input type="email" value={form.contact_email} onChange={set("contact_email")} className={inputCls} placeholder="you@example.com"
              data-testid="contrib-email" />
          </Field>
          <p className="flex items-start gap-1.5 rounded-lg bg-parchment/60 px-3 py-2 text-xs text-muted-foreground" data-testid="contrib-review-notice">
            <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-oldglory-red/70" />
            <T>Submissions are reviewed by our team before they appear. High-risk topics (safety, elections, health) get extra review and never publish automatically.</T>
          </p>
          <div className="flex justify-end">
            <button type="submit" disabled={submitting}
              className="inline-flex items-center gap-2 rounded-full bg-navy px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-navy-800 disabled:opacity-60"
              data-testid="contrib-submit">
              <Send className="h-4 w-4" /> <T>{submitting ? "Sharing…" : "Share with the community"}</T>
            </button>
          </div>
        </motion.form>
      )}

      {items.length > 0 ? (
        <div className="space-y-4" data-testid="contribution-list">
          {items.map((c) => (
            <article key={c.id} className="rounded-xl border border-border bg-white p-5" data-testid={`contribution-${c.id}`}>
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <h3 className="flex items-center gap-2 font-display text-lg font-bold text-navy">
                  <Quote className="h-4 w-4 text-oldglory-red" /> {c.title}
                </h3>
                <span className="rounded-full bg-parchment px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-navy">
                  {langName(c.language)}
                </span>
              </div>
              {c.photo_url && (
                <img src={c.photo_url} alt={c.title} className="mb-3 max-h-72 w-full rounded-lg object-cover" loading="lazy" />
              )}
              <p className="whitespace-pre-line leading-relaxed text-ink/85">{c.content}</p>
              <p className="mt-3 text-xs text-muted-foreground">
                — <span className="font-semibold text-navy">{c.author_name}</span>
                {c.author_role ? `, ${c.author_role}` : ""}
              </p>
            </article>
          ))}
        </div>
      ) : (
        !open && (
          <div className="rounded-xl border border-dashed border-border bg-parchment/40 p-6 text-center text-sm text-muted-foreground" data-testid="contribution-empty">
            <T>No community stories yet. Be the first to share one!</T>
          </div>
        )
      )}
    </section>
  );
}

const inputCls =
  "w-full rounded-lg border border-border bg-white px-3 py-2 text-sm outline-none transition-colors focus:border-navy";

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-navy">
        <T>{label}</T>{required && <span className="text-oldglory-red"> *</span>}
      </span>
      {children}
    </label>
  );
}
